<template>
    <div id="thankbox_occasion" class="field">
        <label for="occasion">Occasion<sup v-if="isRequired" class="font-semibold text-red-500">*</sup></label>
        <div class="control">
            <Multiselect
                id="occasion"
                v-model="modelValue"
                data-cy="thankbox-occasion-select"
                :options="options"
                select-label=""
                deselect-label=""
                :searchable="!isMobileDevice && allowSearching"
                :custom-label="customLabel"
                :allow-empty="false"
                placeholder="Select your occasion"
                class="purple-selector dropdown-top-padding occasion-picker"
                :max-height="500"
                :loading="loadingOccasions"
            >
                <template #singleLabel="{ option }">
                    <span class="flex h-8 items-center space-x-2">
                        <ThankboxIcon :title="option.title" :icon="option.icon" class="h-8" />
                        <span class="line-clamp-1">{{ option.title }}</span>
                    </span>
                </template>
                <template #option="{ option }">
                    <span v-if="option.type === 'occasion'" class="flex items-center space-x-2">
                        <ThankboxIcon :title="option.occasion.title" :icon="option.occasion.icon" class="h-8" />
                        <span>
                            {{ option.occasion.title }}
                        </span>
                    </span>
                    <fieldset v-if="option.type === 'category_title'" class="ml-1 border-t border-gray-200">
                        <legend class="flex items-center pr-2">
                            <span class="text-sm uppercase tracking-[3px] text-dark-gray">{{ option.title }}</span>
                        </legend>
                    </fieldset>
                </template>
                <template #noResult>
                    <span class="flex h-8 items-center space-x-2">
                        Can't find what you’re looking for? Choose "Other…"
                    </span>
                </template>
            </Multiselect>

            <slot name="errors"></slot>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref, useAttrs } from 'vue'
import ThankboxIcon from '@/components/ThankboxIcon.vue'
import Multiselect from 'vue-multiselect'
import { ThankboxOccasion } from 'resources/js/types'
import store from '@/store'
import axios, { AxiosError, AxiosResponse } from 'axios'
import isEmpty from '@/util/isEmpty.ts'

type DropdownType = 'category_title' | 'occasion'

interface DropdownOption<T extends DropdownType> {
    type: T
}

interface OccasionOption extends DropdownOption<'occasion'> {
    occasion: ThankboxOccasion
}

interface CategoryTitle extends DropdownOption<'category_title'> {
    title: string
    $isDisabled: boolean
}

const props = withDefaults(
    defineProps<{
        modelValue: ThankboxOccasion | null
        allowSearching?: boolean
        occasionList?: 'all' | 'message_generation'
        preselectedId?: number
    }>(),
    {
        allowSearching: true,
        occasionList: 'all',
        preselectedId: -1,
    },
)

const attrs = useAttrs()

const emit = defineEmits<{
    (event: 'update:modelValue', value: ThankboxOccasion | null): void
}>()

const modelValue = computed({
    get: () => props.modelValue,
    set: (occasionOption: OccasionOption) => {
        emit('update:modelValue', occasionOption.occasion)
    },
})

const loadingOccasions = ref(false)
const options = ref<(OccasionOption | CategoryTitle)[]>([])

const isMobileDevice = computed(() => window.innerWidth < 768)

const isRequired = computed(() => {
    if ('required' in attrs) {
        // noinspection SuspiciousTypeOfGuard
        if (typeof attrs.required === 'boolean') {
            return attrs.required
        } else {
            return true
        }
    }
    return false
})

const customLabel = (option: OccasionOption | CategoryTitle) => {
    if (option.type === 'category_title') {
        return option.title
    }
    return option.occasion.title
}

onBeforeMount(async () => {
    loadingOccasions.value = true

    try {
        const results = await axios
            .get('/app/api/thankbox-occasions')
            .then((response: AxiosResponse) => {
                const popular: ThankboxOccasion[] = response.data.popular
                const other: ThankboxOccasion[] = response.data.other

                return { popular, other }
            })
            .catch((e: AxiosError<any>) => {
                console.error(e)
            })

        const { popular, other } = results

        // @ts-ignore
        options.value = [
            // @ts-ignore
            { type: 'category_title', title: 'Popular', $isDisabled: true },
            // @ts-ignore
            ...popular
                .filter((occasion: ThankboxOccasion) =>
                    props.occasionList === 'message_generation' ? occasion.available_for_message_generator : true,
                )
                .map((occasion: ThankboxOccasion) => ({ type: 'occasion', occasion: occasion })),
            // @ts-ignore
            { type: 'category_title', title: 'More', $isDisabled: true },
            // @ts-ignore
            ...other
                .filter((occasion: ThankboxOccasion) =>
                    props.occasionList === 'message_generation' ? occasion.available_for_message_generator : true,
                )
                .map((occasion: ThankboxOccasion) => ({ type: 'occasion', occasion: occasion })),
        ]

        if (props.preselectedId > 0) {
            const preselectedOccasion = options.value.find(
                // @ts-ignore
                option => option.type === 'occasion' && option.occasion.id === props.preselectedId,
            ) as OccasionOption
            if (preselectedOccasion) {
                modelValue.value = preselectedOccasion
            }
        }
    } catch (error) {
        console.error(error)
    } finally {
        loadingOccasions.value = false
    }
})
</script>

<style>
.occasion-picker .multiselect__option--disabled {
    background: none !important;
}

.occasion-picker .multiselect__option {
    padding: 0.35rem 0.5rem 0.5rem 0.35rem;
}
</style>
